/**
 * Per-board score ranks.
 *
 * A fixed ladder ("50 points is bronze") cannot work here: boards differ by an
 * order of magnitude. Across the published set an ordinary player scores
 * anywhere from 181 to 1816 depending on the day, and the solver ceiling ranges
 * 6.7k to 27k. A flat threshold would hand out Expert on a rich board and
 * Bronze on a lean one for identical play.
 *
 * So the ladder is derived from the board's OWN anchors, all of which the
 * generator already computes:
 *
 *   casual      what someone scores grabbing the five most obvious words
 *   savvy       what someone scores finding common words and saving the longest
 *   commonMax   beam-search ceiling using only words an ordinary person knows
 *   solverMax   beam-search ceiling over the whole dictionary
 *
 * Mapping, in plain terms:
 *
 *   BRONZE    you got on the board
 *   SILVER    better than grabbing the first words you saw
 *   GOLD      a good ordinary run — decent words, no wasted bullets
 *   PLATINUM  routing heat back over your own tracks is paying off
 *   EXPERT    near the ceiling that normal vocabulary allows
 */
export type Rank = "bronze" | "silver" | "gold" | "platinum" | "expert";

export const RANKS: Rank[] = ["bronze", "silver", "gold", "platinum", "expert"];

/** Ascending score thresholds. Index 0 = bronze … index 4 = expert. */
export type RankThresholds = [number, number, number, number, number];

/** The rank a score earns, or null when it has not reached bronze. */
export function rankFor(score: number, thresholds: RankThresholds): Rank | null {
  let earned: Rank | null = null;
  for (let i = 0; i < RANKS.length; i += 1) {
    if (score >= thresholds[i]) earned = RANKS[i];
  }
  return earned;
}

/** Progress toward the next rank, 0…1, and what that next rank is. */
export function rankProgress(score: number, thresholds: RankThresholds): {
  next: Rank | null;
  fraction: number;
} {
  for (let i = 0; i < RANKS.length; i += 1) {
    if (score < thresholds[i]) {
      const floor = i === 0 ? 0 : thresholds[i - 1];
      const span = Math.max(1, thresholds[i] - floor);
      return { next: RANKS[i], fraction: Math.min(1, Math.max(0, (score - floor) / span)) };
    }
  }
  return { next: null, fraction: 1 };
}
